import FactureDetailModal from '../FactureDetailModal';
import EmailPreviewModal from '../EmailPreviewModal';
import DateSelectionModal from '../DateSelectionModal';
import AjouterFactureModal from './AjouterFactureModal';
import PaymentHistoryModal from '../PaymentHistoryModal';
import EditFactureInterneModal from './EditFactureInterneModal';
import PdfViewerModal from './PdfViewerModal';
import FacturationDetailModal from './FacturationDetailModal';
import ConfirmationModal from '../ConfirmationModal';
import ProgressModal from '../ProgressModal';
import { updateFactureInterne } from '../../services/airtable';

// Dernier jour de chaque trimestre (MM-DD), pour pré-remplir la date d'émission proposée au
// téléchargement d'une facture interne — cf. handleConfirmDownloadFactureInterne, seule étape
// qui fixe désormais réellement la date d'émission.
const QUARTER_END_MONTH_DAY = { Q1: '03-31', Q2: '06-30', Q3: '09-30', Q4: '12-31' };

function getQuarterEndDate(trimestre, annee) {
  if (!trimestre || !annee || !QUARTER_END_MONTH_DAY[trimestre]) return null;
  return `${annee}-${QUARTER_END_MONTH_DAY[trimestre]}`;
}

/**
 * FacturationModals - Composant regroupant toutes les modales de facturation
 *
 * Extrait de FacturationTable.js pour réduire sa taille.
 * Gère 11 modales différentes liées à la gestion des factures.
 *
 * @param {Object} props
 * @param {Object} props.state - État du composant parent
 * @param {Function} props.dispatch - Dispatch du reducer
 * @param {Object} props.ACTIONS - Actions du reducer
 * @param {Object} props.handlers - Handlers pour les actions
 * @param {Object} props.data - Données (prestataires, consultants, etc.)
 * @param {Object} props.modalsState - État des modales (progressModal, statusChangeConfirmation, etc.)
 * @param {Function} props.loadData - Fonction de rechargement des données
 * @param {Function} props.showToast - Fonction d'affichage des toasts
 */
export default function FacturationModals({
  state,
  dispatch,
  ACTIONS,
  handlers,
  data,
  modalsState,
  loadData,
  showToast
}) {
  const {
    activeTab,
    showDetailModal,
    selectedFacture,
    isModalReadOnly,
    showEditFactureInterneModal,
    editingFactureInterne,
    showDateEmissionModal,
    dateEmissionFacture,
    showEmailPreviewModal,
    emailFacture,
    emailPrestataire,
    showPaymentModal,
    selectedPaymentFacture,
    showPaymentHistoryModal,
    selectedPaymentFactureInterne,
    showAjouterModal,
    typeFacture,
    showPdfViewer,
    pdfUrl,
    showDateDownloadInterneModal,
    selectedDownloadFactureInterne
  } = state;

  const { prestataires, consultants, facturationDetailData } = data;
  const { progressModal, setProgressModal, statusChangeConfirmation, setStatusChangeConfirmation, showFacturationDetailModal, setShowFacturationDetailModal } = modalsState;

  const {
    handleConfirmDateEmission,
    handleConfirmSendEmail,
    handleRetrySendEmail,
    handleMarkAsPaid,
    handleCloseEditFactureInterneModal,
    handleSaveFactureInterne,
    handleConfirmDownloadFactureInterne,
    handleClosePdfViewer,
    handleAjouterFacture
  } = handlers;

  // Date d'émission proposée par défaut au téléchargement d'une facture interne : le dernier
  // jour du trimestre facturé, plafonné à aujourd'hui (le trimestre en cours n'est pas encore
  // terminé, on ne peut pas émettre à une date future). Si une date a déjà été fixée lors d'un
  // téléchargement précédent, on la reprend telle quelle plutôt que de la recalculer.
  const today = new Date().toISOString().split('T')[0];
  const quarterEndDate = getQuarterEndDate(
    selectedDownloadFactureInterne?.fields?.Trimestre,
    selectedDownloadFactureInterne?.fields?.Année
  );
  const defaultDownloadDate = selectedDownloadFactureInterne?.fields?.['Date émission']
    || (quarterEndDate ? (quarterEndDate <= today ? quarterEndDate : today) : today);

  return (
    <>
      {/* Modale détail facture */}
      {showDetailModal && selectedFacture && (
        <FactureDetailModal
          isOpen={showDetailModal}
          numeroFacture={selectedFacture}
          readOnly={isModalReadOnly}
          onClose={() => dispatch({ type: ACTIONS.CLOSE_DETAIL_MODAL })}
          onSave={loadData}
        />
      )}

      {/* Modale date d'émission */}
      <DateSelectionModal
        isOpen={showDateEmissionModal}
        onClose={() => dispatch({ type: ACTIONS.CLOSE_DATE_EMISSION_MODAL })}
        onConfirm={handleConfirmDateEmission}
        currentDate={dateEmissionFacture?.fields?.['Date émission']}
        title="Date d'émission de la facture"
        description={`Facture ${dateEmissionFacture?.fields?.['N° facture']}`}
        confirmButtonText="Confirmer et envoyer"
        showTodayOption={true}
        maxDate={new Date().toISOString().split('T')[0]}
      />

      {/* Modale date paiement facture externe */}
      <DateSelectionModal
        isOpen={showPaymentModal}
        onClose={() => dispatch({ type: ACTIONS.CLOSE_PAYMENT_MODAL })}
        onConfirm={handleMarkAsPaid}
        currentDate={selectedPaymentFacture?.fields?.['Date paiement'] || new Date().toISOString().split('T')[0]}
        title="Date de paiement"
        description={`Facture ${selectedPaymentFacture?.fields?.['N° facture']}`}
        confirmButtonText="Marquer comme payée"
        showTodayOption={true}
        maxDate={new Date().toISOString().split('T')[0]}
      />

      {/* Modale date téléchargement facture interne */}
      <DateSelectionModal
        isOpen={showDateDownloadInterneModal}
        onClose={() => dispatch({ type: ACTIONS.CLOSE_DATE_DOWNLOAD_INTERNE_MODAL })}
        onConfirm={handleConfirmDownloadFactureInterne}
        currentDate={defaultDownloadDate}
        title="Date d'émission de la facture"
        description={`Facture ${selectedDownloadFactureInterne?.fields?.['N° facture']}`}
        confirmButtonText="Confirmer et télécharger"
        showTodayOption={false}
        maxDate={today}
      />

      {/* Modale aperçu email */}
      <EmailPreviewModal
        isOpen={showEmailPreviewModal}
        facture={emailFacture}
        prestataire={emailPrestataire}
        onClose={() => dispatch({ type: ACTIONS.CLOSE_EMAIL_PREVIEW_MODAL })}
        onConfirm={handleConfirmSendEmail}
      />

      {/* Modale édition facture interne */}
      <EditFactureInterneModal
        isOpen={showEditFactureInterneModal}
        onClose={handleCloseEditFactureInterneModal}
        facture={editingFactureInterne}
        onSave={handleSaveFactureInterne}
        onChange={(field, value) => dispatch({ type: ACTIONS.UPDATE_EDITING_FACTURE_INTERNE, payload: { field, value } })}
        onStatusChange={(newStatut) => setStatusChangeConfirmation({ isOpen: true, newStatut, loading: false })}
        prestataires={prestataires}
      />

      {/* Modale confirmation changement statut */}
      <ConfirmationModal
        isOpen={statusChangeConfirmation.isOpen}
        onClose={() => setStatusChangeConfirmation({ isOpen: false, newStatut: null, loading: false })}
        onConfirm={async () => {
          setStatusChangeConfirmation(prev => ({ ...prev, loading: true }));
          try {
            if (editingFactureInterne?.id) {
              await updateFactureInterne(editingFactureInterne.id, { 'Statut': statusChangeConfirmation.newStatut });
              showToast(`Statut mis à jour : ${statusChangeConfirmation.newStatut}`, 'success');
              await loadData();
              handleCloseEditFactureInterneModal();
            }
          } catch (err) {
            showToast('Erreur changement de statut', 'error');
          } finally {
            setStatusChangeConfirmation({ isOpen: false, newStatut: null, loading: false });
          }
        }}
        title="Confirmer la validation"
        message={`Voulez-vous passer cette facture au statut "${statusChangeConfirmation.newStatut}" ?`}
        confirmText="Confirmer"
        cancelText="Annuler"
        variant="warning"
        loading={statusChangeConfirmation.loading}
        invertButtons
      />

      {/* Modale progression */}
      <ProgressModal
        isOpen={progressModal.isOpen}
        title={progressModal.title}
        numeroFacture={progressModal.numeroFacture}
        steps={progressModal.steps}
        errorMessage={progressModal.errorMessage}
        onClose={() => setProgressModal({ isOpen: false, title: '', numeroFacture: null, steps: [], errorMessage: null })}
        onRetry={handleRetrySendEmail}
      />

      {/* Modale visualisation PDF */}
      <PdfViewerModal
        isOpen={showPdfViewer}
        onClose={handleClosePdfViewer}
        pdfUrl={pdfUrl}
        title="Aperçu de la facture"
      />

      {/* Modale ajout facture */}
      <AjouterFactureModal
        isOpen={showAjouterModal}
        onClose={() => dispatch({ type: ACTIONS.CLOSE_AJOUTER_MODAL })}
        onSubmit={(data) => handleAjouterFacture(data, typeFacture)}
        type={typeFacture}
        prestataires={prestataires}
        consultants={consultants}
      />

      {/* Modale historique paiements */}
      <PaymentHistoryModal
        isOpen={showPaymentHistoryModal}
        onClose={() => dispatch({ type: ACTIONS.CLOSE_PAYMENT_HISTORY_MODAL })}
        facture={selectedPaymentFactureInterne}
        onPaymentAdded={loadData}
      />

      {/* Modale détail facturation */}
      <FacturationDetailModal
        isOpen={showFacturationDetailModal}
        onClose={() => setShowFacturationDetailModal(false)}
        type={activeTab}
        detailData={facturationDetailData}
      />
    </>
  );
}
